// Ported from the native app's SessionMath.swift. All session math is done
// against wall-clock time in America/New_York, via Intl.DateTimeFormat
// (which — like Swift's Calendar/TimeZone — handles US/UK/JP DST shifts
// correctly even though they don't all change on the same date) rather than
// manual UTC offsetting.

export const ET_ZONE = "America/New_York";

interface ETComponents {
  weekday: number; // 1 = Sunday ... 7 = Saturday, matching Swift's Calendar.component(.weekday)
  hour: number;
  minute: number;
  second: number;
}

const weekdayIndex: Record<string, number> = {
  Sun: 1,
  Mon: 2,
  Tue: 3,
  Wed: 4,
  Thu: 5,
  Fri: 6,
  Sat: 7,
};

const etFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_ZONE,
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** Wall-clock components of `date` in the given IANA zone (ET by default). */
export function etComponents(date: Date): ETComponents {
  const parts = etFormatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "0";
  const weekday = weekdayIndex[get("weekday")] ?? 1;
  // hour12: false can still emit "24" for midnight in some locales/engines —
  // normalize that to 0 the way a 24h wall clock actually reads.
  const hour = Number(get("hour")) % 24;
  return {
    weekday,
    hour,
    minute: Number(get("minute")),
    second: Number(get("second")),
  };
}

/** Minutes since Sunday 00:00:00 ET (fractional, includes seconds). */
export function minuteOfWeekExact(date: Date): number {
  const { weekday, hour, minute, second } = etComponents(date);
  const dayIndex = weekday - 1;
  return dayIndex * 1440 + hour * 60 + minute + second / 60;
}

// --- US market holidays (curated, like the FOMC/CPI tables) ---------------

const etDayFormatter = new Intl.DateTimeFormat("en-CA", { timeZone: ET_ZONE });

/** "YYYY-MM-DD" of `date` as an Eastern-time wall date. */
export function etDateString(date: Date): string {
  return etDayFormatter.format(date);
}

/** NYSE full-closure days. Published a year ahead by the exchange —
 * refresh annually, same maintenance rule as the FOMC/CPI tables. */
export const usMarketHolidays: string[] = [
  "2026-01-01", // New Year's Day
  "2026-01-19", // Martin Luther King Jr. Day
  "2026-02-16", // Washington's Birthday
  "2026-04-03", // Good Friday
  "2026-05-25", // Memorial Day
  "2026-06-19", // Juneteenth
  "2026-07-03", // Independence Day (observed — Jul 4 is a Saturday)
  "2026-09-07", // Labor Day
  "2026-11-26", // Thanksgiving
  "2026-12-25", // Christmas
];
const holidaySet = new Set(usMarketHolidays);

export function isUSMarketHoliday(date: Date): boolean {
  return holidaySet.has(etDateString(date));
}

// --- Instrument-aware session open/close marks (D-092) ---------------------

/** DST-correct epoch ms for an ET wall-clock date/time (probe-and-correct, like
 * the econ-calendar). */
export function etInstant(year: number, month: number, day: number, hour: number, minute: number): number {
  let guess = Date.UTC(year, month - 1, day, hour, minute);
  for (let i = 0; i < 2; i++) {
    const c = etComponents(new Date(guess));
    const seen = c.hour * 60 + c.minute;
    const want = hour * 60 + minute;
    let diff = want - seen;
    if (diff > 720) diff -= 1440; else if (diff < -720) diff += 1440; // shortest wrap
    if (diff === 0) break;
    guess += diff * 60_000;
  }
  return guess;
}

export type MarketClass = "crypto" | "forex" | "futures" | "index" | "stock";
export interface SessionEdge { t: number; kind: "open" | "close"; label: string; }

/** Market open/close instants within [fromMs, toMs] for a market class, ET/DST-
 * correct (D-092). Crypto returns [] (never closes). Weekday: 1=Sun … 7=Sat.
 * - stock / index: NYSE 9:30 → 16:00 ET, Mon–Fri, skipping US market holidays.
 * - forex (incl. spot gold): weekly open Sun 17:00 ET → close Fri 17:00 ET.
 * - futures: CME Globex — Sun open 18:00; Mon–Thu daily break (close 17:00,
 *   reopen 18:00); Fri close 17:00 ET. */
export function sessionEdges(cls: MarketClass, fromMs: number, toMs: number): SessionEdge[] {
  const out: SessionEdge[] = [];
  if (cls === "crypto") return out;
  const seen = new Set<string>();
  for (let t = fromMs - 86_400_000; t <= toMs + 86_400_000; t += 43_200_000) {
    const ds = etDateString(new Date(t)); // "YYYY-MM-DD" ET
    if (seen.has(ds)) continue;
    seen.add(ds);
    const parts = ds.split("-");
    const y = Number(parts[0]), mo = Number(parts[1]), d = Number(parts[2]);
    const noon = new Date(etInstant(y, mo, d, 12, 0));
    const wd = etComponents(noon).weekday; // 1=Sun … 7=Sat
    const hol = isUSMarketHoliday(noon);
    if (cls === "stock" || cls === "index") {
      if (wd >= 2 && wd <= 6 && !hol) {
        out.push({ t: etInstant(y, mo, d, 9, 30), kind: "open", label: "Open" });
        out.push({ t: etInstant(y, mo, d, 16, 0), kind: "close", label: "Close" });
      }
    } else if (cls === "forex") {
      if (wd === 1) out.push({ t: etInstant(y, mo, d, 17, 0), kind: "open", label: "Week open" });
      if (wd === 6) out.push({ t: etInstant(y, mo, d, 17, 0), kind: "close", label: "Week close" });
    } else if (cls === "futures") {
      if (wd === 1) out.push({ t: etInstant(y, mo, d, 18, 0), kind: "open", label: "Open" });
      else if (wd >= 2 && wd <= 5) {
        out.push({ t: etInstant(y, mo, d, 17, 0), kind: "close", label: "Break" });
        out.push({ t: etInstant(y, mo, d, 18, 0), kind: "open", label: "Open" });
      } else if (wd === 6) out.push({ t: etInstant(y, mo, d, 17, 0), kind: "close", label: "Close" });
    }
  }
  return out.filter((e) => e.t >= fromMs && e.t <= toMs).sort((a, b) => a.t - b.t);
}

/** The only days the 24h markets (FX, Globex) truly halt outright. On the
 * other US holidays they run shortened schedules we deliberately do NOT
 * model hour-by-hour — statuses carry an honest note instead. */
function isFullCloseHoliday(date: Date): boolean {
  const day = etDateString(date).slice(5);
  return day === "12-25" || day === "01-01";
}

export interface MarketInterval {
  start: number; // minute of week, ET
  end: number; // minute of week, ET (may exceed 10080 for wraparound)
  label: string;
  isOpen: boolean;
}

/**
 * CME Globex schedule for GC (gold futures) / Topstep-tracked hours: opens
 * Sunday 6:00pm ET, daily maintenance halt 5:00-6:00pm ET Mon-Thu, closes
 * for the weekend Friday 5:00pm ET until Sunday 6:00pm ET.
 */
export function globexIntervals(): MarketInterval[] {
  const intervals: MarketInterval[] = [];
  const sundayOpen = 18 * 60; // 1080
  const fridayClose = 5 * 1440 + 17 * 60; // 8220

  let cursor = sundayOpen;
  for (let day = 1; day <= 4; day++) {
    // Mon..Thu
    const haltStart = day * 1440 + 17 * 60;
    const haltEnd = day * 1440 + 18 * 60;
    intervals.push({ start: cursor, end: haltStart, label: "Open", isOpen: true });
    intervals.push({ start: haltStart, end: haltEnd, label: "Maintenance Break", isOpen: false });
    cursor = haltEnd;
  }
  intervals.push({ start: cursor, end: fridayClose, label: "Open", isOpen: true });
  // Weekend closure wraps past the end of the week array (10080) back to
  // sundayOpen next week.
  intervals.push({
    start: fridayClose,
    end: sundayOpen + 7 * 1440,
    label: "Closed – Weekend",
    isOpen: false,
  });
  return intervals;
}

export interface MarketStatus {
  label: string;
  isOpen: boolean;
  minutesRemaining: number;
  /** Set on US holidays when the market runs but on a shortened schedule
   * we don't model hour-by-hour — surface it, don't fake precision. */
  note?: string;
}

export function currentGlobexStatus(now: Date): MarketStatus {
  // Christmas / New Year's Day: Globex is genuinely dark until the 6pm ET
  // reopen. The other US holidays trade shortened hours (noted below).
  if (isFullCloseHoliday(now)) {
    const { hour, minute, second } = etComponents(now);
    const minuteOfDay = hour * 60 + minute + second / 60;
    if (minuteOfDay < 18 * 60) {
      return { label: "Closed – Holiday", isOpen: false, minutesRemaining: 18 * 60 - minuteOfDay };
    }
  }
  const mow = minuteOfWeekExact(now);
  const intervals = globexIntervals();
  for (const interval of intervals) {
    for (const shift of [0, 7 * 1440]) {
      // The wraparound interval extends past the week's end — check "now"
      // both as-is and shifted a week forward.
      const shifted = mow + shift;
      if (shifted >= interval.start && shifted < interval.end) {
        const result: MarketStatus = { label: interval.label, isOpen: interval.isOpen, minutesRemaining: interval.end - shifted };
        if (interval.isOpen && isUSMarketHoliday(now)) result.note = "US holiday — shortened Globex hours likely";
        return result;
      }
    }
  }
  return { label: "Unknown", isOpen: false, minutesRemaining: 0 };
}

export interface TradingSession {
  name: string;
  startHourET: number; // 24h ET
  endHourET: number; // 24h ET (may be < start, meaning it crosses midnight)
  /** ET weekdays (1 = Sunday … 7 = Saturday) on which the session STARTS.
   * Tokyo's Monday session starts Sunday evening ET, so its start days run
   * Sun–Thu; there is no Tokyo session starting Friday or Saturday ET. */
  startDays: number[];
}

export const tradingSessions: TradingSession[] = [
  { name: "Asia (Tokyo)", startHourET: 19, endHourET: 4, startDays: [1, 2, 3, 4, 5] },
  { name: "London", startHourET: 3, endHourET: 12, startDays: [2, 3, 4, 5, 6] },
  { name: "New York", startHourET: 8, endHourET: 17, startDays: [2, 3, 4, 5, 6] },
];

export interface SessionStatus {
  session: TradingSession;
  isActive: boolean;
  minutesRemaining: number; // until it ends if active, until it starts if not
}

/**
 * Weekday-aware session status: each session exists only as its weekly
 * occurrences (no Tokyo "open" on a Saturday — the bug this replaced).
 * FX halts outright only on Christmas / New Year's Day; those days report
 * inactive with the countdown to the next real occurrence.
 */
export function status(session: TradingSession, now: Date): SessionStatus {
  const mow = minuteOfWeekExact(now);
  const startM = session.startHourET * 60;
  const endM = session.endHourET * 60;
  const duration = session.endHourET <= session.startHourET ? 1440 - startM + endM : endM - startM;
  const halted = isFullCloseHoliday(now);

  let untilNext = Infinity;
  for (const day of session.startDays) {
    const occurrenceStart = (day - 1) * 1440 + startM;
    // This week's slot and next week's (for countdowns across the weekend).
    for (const shift of [0, 7 * 1440]) {
      const start = occurrenceStart + shift;
      if (!halted && mow >= start && mow < start + duration) {
        return { session, isActive: true, minutesRemaining: start + duration - mow };
      }
      if (start > mow) untilNext = Math.min(untilNext, start - mow);
    }
  }
  return { session, isActive: false, minutesRemaining: Number.isFinite(untilNext) ? untilNext : 0 };
}

export interface StockMarketStatus {
  isOpen: boolean;
  minutesRemaining: number;
}

/**
 * NYSE/Nasdaq regular trading session: Mon-Fri 9:30am-4:00pm ET, closed
 * on the curated exchange holidays (full-day closures; early-close days
 * like Black Friday are not modeled — the close countdown just runs long
 * on those two afternoons a year).
 */
export function currentNyseStatus(now: Date): StockMarketStatus {
  const { weekday, hour, minute, second } = etComponents(now); // 1 = Sunday ... 7 = Saturday
  const minuteOfDay = hour * 60 + minute + second / 60;

  const openM = 9 * 60 + 30;
  const closeM = 16 * 60;
  const isTradingDay = (d: Date) => {
    const wd = etComponents(d).weekday;
    return wd >= 2 && wd <= 6 && !isUSMarketHoliday(d);
  };

  if (isTradingDay(now) && minuteOfDay >= openM && minuteOfDay < closeM) {
    return { isOpen: true, minutesRemaining: closeM - minuteOfDay };
  }
  if (isTradingDay(now) && minuteOfDay < openM) {
    return { isOpen: false, minutesRemaining: openM - minuteOfDay };
  }

  // Count whole ET days forward to the next trading day's open. (Day
  // steps are wall-clock approximate across a DST switch — the countdown
  // can be off by an hour twice a year, which the UI rounds away.)
  let minutesToAdd = 1440 - minuteOfDay;
  for (let offset = 1; offset <= 14; offset++) {
    const candidate = new Date(now.getTime() + offset * 86400_000);
    if (isTradingDay(candidate)) break;
    minutesToAdd += 1440;
  }
  minutesToAdd += openM;
  return { isOpen: false, minutesRemaining: minutesToAdd };
}

export function formatDuration(minutes: number): string {
  const totalSeconds = Math.max(0, Math.floor(minutes * 60));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return `${h}h ${String(m).padStart(2, "0")}m`;
  }
  return `${m}m ${String(s).padStart(2, "0")}s`;
}
